import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { Search, ChevronRight, ArrowLeft, Monitor, Smartphone, Tablet } from 'lucide-react';

/**
 * Two questions, one event stream.
 *
 * Sessions answers "what happened to this one customer" — type an email and
 * read their visit in order. Overview answers "where is everyone dropping
 * off". Support is the tab that opens first, because that is the one you need
 * at 9pm when somebody says it did not work.
 */

const STEP_HINT = {
  page_view: 'Opened a page',
  product_view: 'Looked at a product',
  add_to_cart: 'Added to cart',
  cart_view: 'Opened the cart',
  checkout_view: 'Reached checkout',
  order_placed: 'Placed an order',
  search: 'Searched',
  error: 'Hit an error',
  click: 'Tapped',
};

const money = (c) => (c == null ? '' : `$${(c / 100).toFixed(2).replace(/\.00$/, '')}`);

const when = (iso) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

const deviceIcon = (d) =>
  d === 'mobile' ? <Smartphone size={14} /> : d === 'tablet' ? <Tablet size={14} /> : <Monitor size={14} />;

function Card({ children, style }) {
  return (
    <div className="sv-card" style={{ padding: 16, ...style }}>{children}</div>
  );
}

function SessionTrail({ sessionId, onBack }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    supabase
      .from('sv_events')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .then(({ data }) => { setEvents(data || []); setLoading(false); });
  }, [sessionId]);

  const first = events[0];

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
          fontWeight: 700, color: 'var(--accent)', padding: '4px 0', marginBottom: 14,
        }}
      >
        <ArrowLeft size={16} /> All sessions
      </button>

      {first && (
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, fontSize: 13 }}>
            <div><div style={{ opacity: 0.55 }}>Who</div><strong>{first.email || 'Not identified'}</strong></div>
            <div><div style={{ opacity: 0.55 }}>Came from</div><strong>{first.utm_source || first.referrer || 'Direct'}</strong></div>
            <div><div style={{ opacity: 0.55 }}>Device</div><strong>{first.device} · {first.viewport}</strong></div>
            <div><div style={{ opacity: 0.55 }}>Started</div><strong>{when(first.created_at)}</strong></div>
          </div>
          {first.user_agent && (
            <div style={{ fontSize: 11, opacity: 0.5, marginTop: 10, wordBreak: 'break-word' }}>{first.user_agent}</div>
          )}
        </Card>
      )}

      {loading && <p style={{ opacity: 0.6, fontSize: 14 }}>Loading the trail…</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {events.map((e, i) => {
          const prev = events[i - 1];
          const gapMs = prev ? new Date(e.created_at) - new Date(prev.created_at) : 0;
          const gap = gapMs > 20000 ? `${Math.round(gapMs / 1000)}s later` : null;
          const isError = e.event_type === 'error';
          return (
            <div key={e.id}>
              {gap && (
                <div style={{ fontSize: 11, opacity: 0.4, padding: '2px 0 2px 14px' }}>{gap}</div>
              )}
              <div style={{
                display: 'flex', gap: 12, padding: '10px 12px', borderRadius: 10,
                background: isError ? '#fef2f2' : 'transparent',
                borderLeft: `3px solid ${isError ? '#dc2626' : 'var(--border)'}`,
              }}>
                <div style={{ fontSize: 12, opacity: 0.5, minWidth: 58, flexShrink: 0 }}>
                  {new Date(e.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isError ? '#991b1b' : 'inherit' }}>
                    {STEP_HINT[e.event_type] || e.event_type}
                    {e.label ? <span style={{ fontWeight: 500 }}> — {e.label}</span> : null}
                    {e.value_cents != null ? <span style={{ fontWeight: 500, opacity: 0.7 }}> · {money(e.value_cents)}</span> : null}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.55, marginTop: 2, wordBreak: 'break-all' }}>
                    {e.path}
                    {e.meta ? ` · ${JSON.stringify(e.meta)}` : ''}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && events.length === 0 && (
        <p style={{ opacity: 0.6, fontSize: 14 }}>Nothing recorded for that session.</p>
      )}
    </div>
  );
}

function SessionsTab() {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSession, setOpenSession] = useState(null);

  const load = useCallback((term) => {
    setLoading(true);
    supabase.rpc('sv_sessions', { p_search: term || null, p_limit: 50 })
      .then(({ data }) => { setRows(data || []); setLoading(false); });
  }, []);

  useEffect(() => { load(''); }, [load]);

  if (openSession) {
    return <SessionTrail sessionId={openSession} onBack={() => setOpenSession(null)} />;
  }

  return (
    <div>
      <form
        onSubmit={(e) => { e.preventDefault(); load(search.trim()); }}
        style={{ display: 'flex', gap: 8, marginBottom: 16 }}
      >
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 13, opacity: 0.45 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Email, order number or session id"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 12px 11px 36px',
              borderRadius: 10, border: '1px solid var(--border)', fontFamily: 'inherit',
              fontSize: 16, background: 'var(--bg-elevated)', color: 'var(--text)',
            }}
          />
        </div>
        <button
          type="submit"
          style={{
            background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10,
            padding: '0 20px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Find
        </button>
      </form>

      {loading && <p style={{ opacity: 0.6, fontSize: 14 }}>Loading…</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r) => (
          <button
            key={r.session_id}
            onClick={() => setOpenSession(r.session_id)}
            className="sv-card"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
              padding: 14, border: '1px solid var(--border)', cursor: 'pointer',
              fontFamily: 'inherit', background: 'var(--surface)', color: 'var(--text)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {r.email || 'Anonymous visitor'}
                {r.ordered && (
                  <span style={{
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                    background: '#16a34a', color: '#fff', padding: '2px 7px', borderRadius: 999,
                  }}>Ordered</span>
                )}
              </div>
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {deviceIcon(r.device)}
                {r.events} events · from {r.utm_source || r.referrer || 'Direct'} · landed {r.landed_on} · {when(r.started_at)}
              </div>
            </div>
            <ChevronRight size={18} style={{ opacity: 0.4, flexShrink: 0 }} />
          </button>
        ))}
      </div>

      {!loading && rows.length === 0 && (
        <p style={{ opacity: 0.6, fontSize: 14 }}>
          No sessions yet. They start appearing as soon as someone visits the store.
        </p>
      )}
    </div>
  );
}

function OverviewTab() {
  const [days, setDays] = useState(30);
  const [funnel, setFunnel] = useState([]);
  const [sources, setSources] = useState([]);
  const [searches, setSearches] = useState([]);

  useEffect(() => {
    supabase.rpc('sv_funnel', { p_days: days }).then(({ data }) => setFunnel(data || []));
    supabase.rpc('sv_sources', { p_days: days }).then(({ data }) => setSources(data || []));
    supabase.rpc('sv_searches', { p_days: days }).then(({ data }) => setSearches(data || []));
  }, [days]);

  const top = funnel[0]?.sessions || 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 13, fontWeight: 700,
              border: days === d ? '2px solid var(--accent)' : '1px solid var(--border)',
              background: days === d ? 'var(--bg-elevated)' : 'transparent',
              color: 'var(--text)',
            }}
          >
            {d} days
          </button>
        ))}
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55, marginBottom: 12 }}>
          Funnel
        </div>
        {funnel.map((f) => {
          const pct = top ? Math.round((f.sessions / top) * 100) : 0;
          return (
            <div key={f.step} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span style={{ fontWeight: 700 }}>{f.step}</span>
                <span style={{ opacity: 0.7 }}>{f.sessions}{top ? ` · ${pct}%` : ''}</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)' }} />
              </div>
            </div>
          );
        })}
        {funnel.length === 0 && <p style={{ opacity: 0.6, fontSize: 14, margin: 0 }}>No data in this window yet.</p>}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
        <Card>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55, marginBottom: 10 }}>
            Where they came from
          </div>
          {sources.map((s) => (
            <div key={s.source} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <span>{s.source}</span>
              <span style={{ opacity: 0.7 }}>{s.sessions}{s.orders > 0 ? ` · ${s.orders} ordered` : ''}</span>
            </div>
          ))}
          {sources.length === 0 && <p style={{ opacity: 0.6, fontSize: 14, margin: 0 }}>Nothing yet.</p>}
        </Card>

        <Card>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55, marginBottom: 10 }}>
            What they searched
          </div>
          {searches.map((s) => (
            <div key={s.term} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <span>{s.term}</span>
              <span style={{ opacity: 0.7 }}>{s.times}</span>
            </div>
          ))}
          {searches.length === 0 && <p style={{ opacity: 0.6, fontSize: 14, margin: 0 }}>Nothing yet.</p>}
        </Card>
      </div>
    </div>
  );
}

export default function AdminAnalyticsPage() {
  // Support first. The aggregate view is useful weekly; the trail is what you
  // need the moment a customer says something went wrong.
  const [tab, setTab] = useState('sessions');

  return (
    <div>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 900, marginBottom: 6 }}>Analytics</h1>
      <p style={{ fontSize: 14, opacity: 0.6, marginTop: 0, marginBottom: 18 }}>
        Every visit to the store, and what people did on it.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {[['sessions', 'Sessions'], ['overview', 'Overview']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '9px 18px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 14, fontWeight: 700,
              border: tab === key ? '2px solid var(--accent)' : '1px solid var(--border)',
              background: tab === key ? 'var(--bg-elevated)' : 'transparent',
              color: 'var(--text)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'sessions' ? <SessionsTab /> : <OverviewTab />}
    </div>
  );
}
